/**
 * The photo pipeline, entirely in the browser (Canvas 2D). It stands in for
 * the office's Photoshop actions: resize, an automatic tonal correction, light
 * sharpening, and the logo in a corner.
 *
 * Every photo is processed from its original file exactly once on export, so
 * there is a single JPEG encode. The correction itself is measured once, on a
 * small preview, and reused at full size — histogram-based numbers do not
 * depend on the image size, and it means the preview shows what the export
 * will be.
 */

export type Position = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'center'
export type Rotation = 0 | 90 | 180 | 270

export interface Settings {
  /** Longest edge in pixels; null keeps the original size. */
  maxEdge: number | null
  /** 0–1: how much of the automatic correction to apply. */
  enhance: number
  /** Unsharp-mask amount, 0 = off. */
  sharpen: number
  watermark: boolean
  position: Position
  /** Logo width as a fraction of the photo's width. */
  logoSize: number
  /** Distance from the edges, as a fraction of the photo's width. */
  margin: number
  opacity: number
  /** A soft white glow behind the logo, for dark photos. */
  halo: boolean
  /** JPEG quality, 0–1. */
  quality: number
}

export const DEFAULTS: Settings = {
  maxEdge: 1920,
  enhance: 0.8,
  sharpen: 0.35,
  watermark: true,
  position: 'top-right',
  logoSize: 0.2,
  margin: 0.025,
  opacity: 1,
  halo: false,
  quality: 0.9,
}

/** What the automatic correction measured on a photo. */
export interface Correction {
  /** Per channel (r, g, b): the input values mapped to 0 and 255. */
  lo: [number, number, number]
  hi: [number, number, number]
  /** Midtone gamma, < 1 brightens. */
  gamma: number
}

/** The neutral correction: changes nothing. */
export const IDENTITY: Correction = { lo: [0, 0, 0], hi: [255, 255, 255], gamma: 1 }

/** Decode a file, honouring its EXIF orientation. Throws for formats the
 *  browser cannot read (HEIC outside Safari, RAW). */
export const decode = (file: Blob): Promise<ImageBitmap> =>
  createImageBitmap(file, { imageOrientation: 'from-image' })

const canvas = (width: number, height: number) => {
  const c = document.createElement('canvas')
  c.width = Math.max(1, Math.round(width))
  c.height = Math.max(1, Math.round(height))
  return c
}

const context = (c: HTMLCanvasElement) => {
  const ctx = c.getContext('2d', { willReadFrequently: true })
  if (!ctx) throw new Error('canvas 2d context unavailable')
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  return ctx
}

/**
 * The photo, rotated and scaled so its longest edge is at most `maxEdge`.
 * Large reductions go in halving steps: a single big drawImage step aliases
 * fine detail (rigging, railings) into jagged lines.
 */
export function scale(
  bitmap: ImageBitmap,
  rotation: Rotation,
  maxEdge: number | null
): HTMLCanvasElement {
  const turned = rotation % 180 !== 0
  const outW = turned ? bitmap.height : bitmap.width
  const outH = turned ? bitmap.width : bitmap.height
  const factor = maxEdge ? Math.min(1, maxEdge / Math.max(outW, outH)) : 1
  // Target size before rotation.
  const tw = Math.round(bitmap.width * factor)
  const th = Math.round(bitmap.height * factor)

  let source: CanvasImageSource = bitmap
  let sw = bitmap.width
  let sh = bitmap.height
  while (sw / 2 >= tw * 1.5 && sh / 2 >= th * 1.5) {
    const step = canvas(sw / 2, sh / 2)
    context(step).drawImage(source, 0, 0, step.width, step.height)
    source = step
    sw = step.width
    sh = step.height
  }

  const out = turned ? canvas(th, tw) : canvas(tw, th)
  const ctx = context(out)
  // The transform belongs to this one draw. A canvas keeps a single context,
  // so a transform left in place would shift everything drawn later — the logo
  // landed half a photo down and to the right, off the image.
  ctx.save()
  ctx.translate(out.width / 2, out.height / 2)
  ctx.rotate((rotation * Math.PI) / 180)
  ctx.drawImage(source, -tw / 2, -th / 2, tw, th)
  ctx.restore()
  return out
}

/**
 * Measure the automatic correction — what Photoshop's "Auto Tone" does, plus
 * a gentle brightening of dark shots (boat interiors usually are):
 *
 * - per channel, the darkest and lightest 0.2 % of pixels are clipped and the
 *   rest stretched to the full range, which also removes colour casts;
 * - then a midtone gamma pulls the average brightness towards 46 %, bounded so
 *   it never does more than a modest lift or dim.
 */
export function measure(c: HTMLCanvasElement, clip = 0.002): Correction {
  const { data } = context(c).getImageData(0, 0, c.width, c.height)
  const n = data.length / 4
  const hist = [new Uint32Array(256), new Uint32Array(256), new Uint32Array(256)]
  for (let i = 0; i < data.length; i += 4) {
    hist[0][data[i]]++
    hist[1][data[i + 1]]++
    hist[2][data[i + 2]]++
  }
  const cut = n * clip
  const lo = [0, 0, 0] as [number, number, number]
  const hi = [255, 255, 255] as [number, number, number]
  for (let ch = 0; ch < 3; ch++) {
    let sum = 0
    let v = 0
    while (v < 255 && sum + hist[ch][v] <= cut) sum += hist[ch][v++]
    lo[ch] = v
    sum = 0
    v = 255
    while (v > 0 && sum + hist[ch][v] <= cut) sum += hist[ch][v--]
    hi[ch] = v
    // A channel with almost no range (a photo of fog, a blank wall) would be
    // stretched into noise; leave it alone.
    if (hi[ch] - lo[ch] < 32) {
      lo[ch] = 0
      hi[ch] = 255
    }
  }

  // Mean luminance after the stretch.
  const luts = levelLuts({ lo, hi, gamma: 1 }, 1)
  let lum = 0
  for (let i = 0; i < data.length; i += 4) {
    lum += 0.2126 * luts[0][data[i]] + 0.7152 * luts[1][data[i + 1]] + 0.0722 * luts[2][data[i + 2]]
  }
  const mean = Math.min(0.95, Math.max(0.05, lum / n / 255))
  const gamma = Math.min(1.2, Math.max(0.8, Math.log(0.46) / Math.log(mean)))
  return { lo, hi, gamma }
}

/** Lookup tables applying a correction at the given strength (0–1). */
function levelLuts(c: Correction, strength: number): Uint8ClampedArray[] {
  return [0, 1, 2].map((ch) => {
    const lut = new Uint8ClampedArray(256)
    const range = Math.max(1, c.hi[ch] - c.lo[ch])
    for (let v = 0; v < 256; v++) {
      const stretched = Math.min(1, Math.max(0, (v - c.lo[ch]) / range))
      const corrected = 255 * Math.pow(stretched, c.gamma)
      lut[v] = v + strength * (corrected - v)
    }
    return lut
  })
}

/** Apply a measured correction in place. */
export function correct(c: HTMLCanvasElement, correction: Correction, strength: number) {
  if (strength <= 0) return
  const ctx = context(c)
  const img = ctx.getImageData(0, 0, c.width, c.height)
  const d = img.data
  const [r, g, b] = levelLuts(correction, strength)
  for (let i = 0; i < d.length; i += 4) {
    d[i] = r[d[i]]
    d[i + 1] = g[d[i + 1]]
    d[i + 2] = b[d[i + 2]]
  }
  ctx.putImageData(img, 0, 0)
}

/**
 * Unsharp mask with a 3×3 blur: out = in + amount × (in − blur). Run after
 * resizing, which softens.
 */
export function sharpen(c: HTMLCanvasElement, amount: number) {
  if (amount <= 0) return
  const ctx = context(c)
  const w = c.width
  const h = c.height
  const img = ctx.getImageData(0, 0, w, h)
  const src = new Uint8ClampedArray(img.data)
  const d = img.data
  for (let y = 1; y < h - 1; y++) {
    for (let x = 1; x < w - 1; x++) {
      const i = (y * w + x) * 4
      for (let ch = 0; ch < 3; ch++) {
        const k = i + ch
        const up = k - w * 4
        const down = k + w * 4
        const blur =
          (src[up - 4] +
            src[up] +
            src[up + 4] +
            src[k - 4] +
            src[k] +
            src[k + 4] +
            src[down - 4] +
            src[down] +
            src[down + 4]) /
          9
        d[k] = src[k] + amount * (src[k] - blur)
      }
    }
  }
  ctx.putImageData(img, 0, 0)
}

/** The logo's rectangle on a photo of the given size. */
export function logoBox(
  width: number,
  height: number,
  logo: { width: number; height: number },
  s: Pick<Settings, 'position' | 'logoSize' | 'margin'>
) {
  const w = Math.round(width * s.logoSize)
  const h = Math.round((w * logo.height) / logo.width)
  const m = Math.round(width * s.margin)
  const x = s.position.endsWith('left')
    ? m
    : s.position === 'center'
      ? (width - w) / 2
      : width - w - m
  const y = s.position.startsWith('top')
    ? m
    : s.position === 'center'
      ? (height - h) / 2
      : height - h - m
  return { x: Math.round(x), y: Math.round(y), w, h }
}

/**
 * The same rectangle as fractions of the photo's width and height, for a photo
 * of this aspect ratio (width / height): what is stored with a saved photo
 * (attachments.logo_box), so the social kit knows the logo is already there.
 */
export function logoArea(
  aspect: number,
  logo: { width: number; height: number },
  s: Pick<Settings, 'position' | 'logoSize' | 'margin'>
) {
  // Worked out on a large photo, so the rounding to pixels does not show.
  const width = 10_000
  const height = width / aspect
  const box = logoBox(width, height, logo, s)
  const round = (n: number) => Math.round(n * 10_000) / 10_000
  return {
    x: round(box.x / width),
    y: round(box.y / height),
    w: round(box.w / width),
    h: round(box.h / height),
  }
}

export function watermark(
  c: HTMLCanvasElement,
  logo: CanvasImageSource & { width: number; height: number },
  s: Settings
) {
  if (!s.watermark) return
  const ctx = context(c)
  const box = logoBox(c.width, c.height, logo, s)
  ctx.save()
  ctx.globalAlpha = s.opacity
  if (s.halo) {
    ctx.shadowColor = 'rgba(255, 255, 255, 0.9)'
    ctx.shadowBlur = Math.max(4, box.w * 0.05)
  }
  ctx.drawImage(logo, box.x, box.y, box.w, box.h)
  ctx.restore()
}

export const toJpeg = (c: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) =>
    c.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('encoding failed'))),
      'image/jpeg',
      quality
    )
  )

/** One photo, start to finish: the export path. */
export async function finish(
  file: Blob,
  rotation: Rotation,
  correction: Correction | null,
  logo: (CanvasImageSource & { width: number; height: number }) | null,
  s: Settings
): Promise<Blob> {
  const bitmap = await decode(file)
  try {
    const c = scale(bitmap, rotation, s.maxEdge)
    if (correction) correct(c, correction, s.enhance)
    sharpen(c, s.sharpen)
    if (logo) watermark(c, logo, s)
    return await toJpeg(c, s.quality)
  } finally {
    bitmap.close()
  }
}
